/**
 * @packageDocumentation
 * Implements the animated win popup overlay, formatting messages, syncing with the logo, and dispatching show/hide events.
 */
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { Assets } from "pixi.js"; 
import { font_main, font_third, HandleOrientation } from "./global";
import { gsap } from "gsap/gsap-core";
import {  currencySymbol, ShowCent } from "./server";
import { LogoAnim } from "../app";
import { BottomBar } from "./bottomBar";
import { Bonus } from "./Bonus";
import { SoundManager } from "./sounds";

const shouldEnableSpinFromPopup = () => {
  const autoplayPopup = (globalThis as any)?.autoplayPopup;
  return !autoplayPopup?.isRunningNow?.();
};

// WinPopup.ts
// Works with PixiJS v7/v8. If you're using v8 + @pixi/spine, adjust the Spine type accordingly.
// If your project exposes `spine.Spine`, keep it as-is; otherwise, change `any` to your Spine type.
// let bonusData = [["power-up",1,4,3,5],[30,"power-up",50],["power-up",100],[1000]];
export type WinPopupOptions = {
  spineId?: string;            // Asset key for spine (e.g., "win_popup_bg_json"/"atlas" preloaded pair via Spine.from)
  spineAnimName?: string;      // Animation to play on show
  //overlayAlpha?: number;       // 0..1
  closeOnTap?: boolean;        // Tap anywhere to close
  winPrefix?: string;          // e.g. "WIN"
  textStyle?:{[key:string]:any} //Partial<PIXI.ITextStyle>;
  width?: number;              // For centering if different from app.screen
  height?: number;
};

export type ShowWinpopupOptions = {
  animName?: string;
  totalAmount?: number;
  prefix?: string;
};

export class WinPopup extends PIXI.Container {
  //private overlay!: PIXI.Graphics;
  public  winPopup_bg_anim!: any;  // use your Spine type if available (e.g., spine.Spine)
  public  winPopupWintext!: PIXI.Text;
  // bonusData = bonusData;
  private opts: Required<WinPopupOptions>;
  private content!: PIXI.Container;
  private isBuilt = false;
  public PopupShowtime!:NodeJS.Timeout;
  public LogoAnim!:Spine;
  private amountTween?: gsap.core.Tween;
  BonusPage!:Bonus;
  onHidden:()=>void = ()=>{};
  constructor(opts?: WinPopupOptions) { 
    super(); 
    this.opts = {
      spineId: "win_popup_bg",           // you will attach your real spine via createWinpopup()
      spineAnimName: "win",              // default animation name
      //overlayAlpha: 0.6,
      closeOnTap: true,
      winPrefix: "TOTAL WIN",
      textStyle: {
        fontFamily: font_third,
        fontSize: 80,
        fontWeight: "900",
        fill: 0xFFD700,
        align: "center", 
        dropShadow: {
            alpha: 0.8,
            angle: 1,
            blur: 4,
            color: '0x111111',
            distance: 2,
        },
        stroke:{
            color:"black",
            width:4,
            join:"round"
        },
      },
      width: 1366,
      height: 768,
      ...opts,
    };

    this.visible = false;
    this.alpha = 0;
    this.sortableChildren = true;

    this.createWinpopup(1366,768,this.spineFactory)
  }

  /**
   * Build the popup. Call once after assets are ready.
   * @param screenW app.screen.width (or your game logical width)
   * @param screenH app.screen.height (or your game logical height)
   * @param spineFactory optional factory to create Spine (use if you don’t use `spine.Spine.from`)
   *    Example: () => spine.Spine.from("win_popup_bg_json","win_popup_bg_atlas")
   */
  createWinpopup(
    screenW: number,
    screenH: number,
    spineFactory?: (src:{skeleton:string,atlas:string},anim:string) => Spine
  ) {
    if (this.isBuilt) return; 
    const W = this.opts.width || screenW;
    const H = this.opts.height || screenH;


    // Content container
    this.content = new PIXI.Container();
    this.content.zIndex = 1;
    this.addChild(this.content); 
    // Spine background animation (optional)
    if (spineFactory) {
      this.winPopup_bg_anim = spineFactory({skeleton:"Total_win_popup_anim_json",atlas:"Total_win_popup_anim_atlas"}, "animation_1");
      this.winPopup_bg_anim.scale.set(0.8);
      this.winPopup_bg_anim.x = 0;
      this.winPopup_bg_anim.y = 0;
      this.winPopup_bg_anim.state.timeScale = 0; 

      this.content.addChild(this.winPopup_bg_anim);
    }

    // Win text
    // this.winPopupWintext = new PIXI.Text(`${this.opts.winPrefix} 0`, this.opts.textStyle);
    this.winPopupWintext = new PIXI.Text({text:`${this.opts.winPrefix}\n0`,style:this.opts.textStyle});
    // this.winPopupWintext.style.fontFamily = font_third
    this.winPopupWintext.anchor.set(0.5);
    this.winPopupWintext.x = 0;
    this.winPopupWintext.y = 0;
    this.content.addChild(this.winPopupWintext);
    this.content.position.set(W / 2, H / 2);

    this.content.scale.set(0.8)
    // gsap.to(this.content,{rotation:360,repeat:-1,duration:10})
    // Close-on-tap (optional)
    if (this.opts.closeOnTap) {
      this.eventMode = "static";
      this.cursor = "pointer";
      this.on("pointertap", () => this.hideWinpopup());
    }
    this.isBuilt = true;
  }
  spineFactory(src:{skeleton:string,atlas:string},anim:string){
    // {skeleton:"Total_win_popup_anim_json",atlas:"Total_win_popup_anim_atlas"}
    const spineAnim = Spine.from({skeleton:src.skeleton,atlas:src.atlas})
    spineAnim.state.setAnimation(0,anim,false) 
    return spineAnim;
  }

  /**
   * Show popup with amount, play animation, and fade in.
   * @param amount number | string
   * @param animName optional animation override
   */
  showWinpopup(amount: number | string, animOrOptions?: string | ShowWinpopupOptions, callback?:()=>void) {
    SoundManager.Win_Popup.play()
    if (!this.isBuilt) {
      console.warn("WinPopup: call createWinpopup() before showWinpopup().");
      return;
    }
    this.onHidden = ()=>{ 
    if(LogoAnim.allsuper){ 
      LogoAnim.insideBonus = true;
      LogoAnim.StartBut_disableFun?.();  
      this.BonusPage.startBonus()      
    }
     if(callback)callback() 
    };
    const rawDisplay = Number(amount);
    const displayValue = Number.isFinite(rawDisplay) ? rawDisplay : 0;

    let options: ShowWinpopupOptions | undefined;
    let animName: string | undefined;
    if (typeof animOrOptions === "string") {
      animName = animOrOptions;
    } else if (animOrOptions) {
      options = animOrOptions;
      animName = options.animName;
    }

    const totalCandidate = options?.totalAmount;
    const totalValue = typeof totalCandidate === "number" && Number.isFinite(totalCandidate) ? totalCandidate : displayValue;

    const prefix = options?.prefix ?? this.opts.winPrefix;
    const totalText = this.formatAmount(totalValue);

    this.winPopup_bg_anim.state.timeScale = 1;

    const tweenLib = (globalThis as any)?.gsap ?? gsap;
    this.amountTween?.kill();
    this.amountTween = undefined;

    const setWinText = (val: number) => {
      const formatted = this.formatAmount(val);
      this.winPopupWintext.text = `${prefix}\n${formatted}`;
    };

    if (tweenLib && displayValue !== 0) {
      const counter = { value: 0 };
      setWinText(0);
      this.amountTween = tweenLib.to(counter, {
        value: displayValue,
        duration: 1,
        ease: "none",
        onUpdate: () => {
          setWinText(counter.value);
        },
        onComplete: () => {
          setWinText(displayValue);
          this.amountTween = undefined;
        },
      });
    } else {
      setWinText(displayValue);
    }

    LogoAnim.totalwinContainer?.setValue(`${totalText}`);
    BottomBar.updateTotalWin(totalValue);

    BottomBar.TotalBalancevalue =
        BottomBar.TotalBalancevalue + BottomBar.TotalWinvalue;
      BottomBar.updateBalance(BottomBar.TotalBalancevalue);

    if (this.winPopup_bg_anim?.state) {
      const playName = animName || this.opts.spineAnimName;
      try {
        this.winPopup_bg_anim.state.setAnimation(0, "animation_1", false);
        this.winPopup_bg_anim.state.addAnimation(0, "animation_2", true);
      } catch {
        // ignore if animation not found
      }
    }

    this.zIndex = 10;
    this.visible = true;
    this.fadeTo(1, 0.25);
    this.winPopupWintext.scale.set(0.9);
    this.scaleTween(this.winPopupWintext, 1.05, 0.18, () => {
      this.scaleTween(this.winPopupWintext, 1.0, 0.12);
    });

    clearTimeout(this.PopupShowtime);

    this.emit("winpopup:show", {
      amount: displayValue,
      totalAmount: totalValue,
      prefix,
    });

    this.PopupShowtime = setTimeout(() => {
      clearTimeout(this.PopupShowtime);
      this.hideWinpopup();
    }, 3000);
  }
  // NomalizeLogo(logo:Spine){
  //   logo.state.setAnimation(0,"idle_anim",true)
  // }

  /**
   * Hide popup, fade out, and keep it built for reuse.
   */
  hideWinpopup() {
    // if(LogoAnim.allsuper){
      
    // }else{
      clearTimeout(this.PopupShowtime);
      this.amountTween?.kill();
      this.amountTween = undefined;

      if (shouldEnableSpinFromPopup()) {
        LogoAnim.StartBut_enableFun?.();
      }
      LogoAnim.state?.setAnimation(0,"idle_anim",true);
      if (this.winPopup_bg_anim?.state) {
        this.winPopup_bg_anim.state.timeScale = 0;
      }

      if (!this.visible) return;
      this.fadeTo(0, 0.2, () => {
        this.visible = false;
        this.emit("winpopup:hidden");
        this.onHidden();
        
      });
    // }
    
  }

  /**
   * Update amount without re-showing.
   */
  setAmount(amount: number | string) {
    if (this.winPopupWintext) {
      this.winPopupWintext.text = `${this.opts.winPrefix}\n${amount}`;
    }
  }

  /**
   * Clean up completely (optional).
   */
  dispose() {
    this.removeAllListeners();
    this.destroy({ children: true });
    this.isBuilt = false;
  }

  // ---------- helpers (GSAP optional) ----------
  private fadeTo(targetAlpha: number, durSec: number, onComplete?: () => void) {
    const anyWin = globalThis as any;
    const gsap = anyWin?.gsap;
    if (gsap) {
      gsap.to(this, { alpha: targetAlpha, duration: durSec, onComplete });
    } else {
      // fallback
      this.alpha = targetAlpha;
      if (onComplete) onComplete();
    }
  }

  private scaleTween(target: PIXI.Text, to: number, durSec: number, onComplete?: () => void) {
    const anyWin = globalThis as any;
    const gsap = anyWin?.gsap;
    if (gsap) {
      gsap.to(target.scale, { x: to, y: to, duration: durSec, ease: "back.out(2)", onComplete });
    } else {
      target.scale.set(to);
      if (onComplete) onComplete();
    }
  }

  private formatAmount(value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    let amountVal = currencySymbol + safeValue.toFixed(2);
    if (ShowCent) {
      if (safeValue < 1) {
        amountVal = Math.floor(safeValue * 100) + "¢";
      }
    }
    return amountVal;
  }

 
}
